import Point from "../models/points/point"

const debug = false
// const debug = "triangulation"

const EARTH_RADIUS = 6378137 // meters (WGS84 equatorial radius)

type LocalPoint = { x: number, y: number }
type LocalObserver = LocalPoint & { r: number }

// Local tangent-plane (equirectangular) projection, accurate enough at the sub-city radii this app deals with.
const toLocalXY = (lat: number, lng: number, originLat: number, originLng: number): LocalPoint => {
    const originLatRad = originLat * Math.PI / 180
    return {
        x: (lng - originLng) * Math.PI / 180 * EARTH_RADIUS * Math.cos(originLatRad),
        y: (lat - originLat) * Math.PI / 180 * EARTH_RADIUS
    }
}

const toLatLng = (point: LocalPoint, originLat: number, originLng: number): { lat: number, lng: number } => {
    const originLatRad = originLat * Math.PI / 180
    return {
        lat: originLat + (point.y / EARTH_RADIUS) * (180 / Math.PI),
        lng: originLng + (point.x / (EARTH_RADIUS * Math.cos(originLatRad))) * (180 / Math.PI)
    }
}

// Standard circle-circle intersection (chord midpoint + perpendicular offset construction).
// Returns null when the circles don't produce a real intersection.
export const intersectTwoCircles = (c1: LocalPoint, r1: number, c2: LocalPoint, r2: number): [LocalPoint, LocalPoint] | null => {
    const dx = c2.x - c1.x
    const dy = c2.y - c1.y
    const d = Math.sqrt(dx * dx + dy * dy)

    if (d === 0) return null
    if (d > r1 + r2) return null
    if (d < Math.abs(r1 - r2)) return null

    const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d)
    const hSq = r1 * r1 - a * a
    if (hSq < 0) return null

    const h = Math.sqrt(hSq)
    const mx = c1.x + a * dx / d
    const my = c1.y + a * dy / d

    return [
        { x: mx + h * dy / d, y: my - h * dx / d },
        { x: mx - h * dy / d, y: my + h * dx / d }
    ]
}

// Linearized least-squares multilateration: reference the first observer, build the linear
// system from pairwise circle-equation differences, solve the 2x2 normal equations in closed form.
// Returns null when the system is degenerate (e.g. all observers collinear).
export const bestFitPoint = (observers: LocalObserver[]): LocalPoint | null => {
    const ref = observers[0]

    let Sxx = 0
    let Sxy = 0
    let Syy = 0
    let Sxb = 0
    let Syb = 0

    _.forEach(observers.slice(1), (o: LocalObserver) => {
        const Ax = 2 * (o.x - ref.x)
        const Ay = 2 * (o.y - ref.y)
        const b  = (o.x * o.x - ref.x * ref.x) + (o.y * o.y - ref.y * ref.y) + (ref.r * ref.r - o.r * o.r)

        Sxx += Ax * Ax
        Sxy += Ax * Ay
        Syy += Ay * Ay
        Sxb += Ax * b
        Syb += Ay * b
    })

    const det = Sxx * Syy - Sxy * Sxy
    // Relative (scale-invariant) degeneracy check: det/(Sxx*Syy) -> 0 as observer directions become collinear,
    // regardless of the absolute magnitude of the coordinates (an absolute epsilon here would never trigger,
    // since Sxx/Syy/det scale with squared meter distances and are orders of magnitude larger than 1e-9).
    if (Sxx * Syy <= 0 || det < 1e-9 * Sxx * Syy) return null

    return {
        x: (Syy * Sxb - Sxy * Syb) / det,
        y: (Sxx * Syb - Sxy * Sxb) / det
    }
}

// Splits observers into connected components where an edge means the two circles intersect.
// Returns arrays of indices into `local`; singletons are dropped.
export const groupByIntersection = (local: LocalObserver[]): number[][] => {
    const visited: boolean[] = []
    const groups: number[][] = []

    _.forEach(local, (_start: LocalObserver, startIndex: number) => {
        if (visited[startIndex]) {
            return
        }
        visited[startIndex] = true
        const group = [startIndex]

        for (let i = 0; i < group.length; i++) {
            const current = local[group[i]]
            _.forEach(local, (other: LocalObserver, otherIndex: number) => {
                if (
                    !visited[otherIndex] &&
                    intersectTwoCircles(current, current.r, other, other.r)
                ) {
                    visited[otherIndex] = true
                    group.push(otherIndex)
                }
                return
            })
        }

        if (group.length > 1) {
            groups.push(group)
        }
        return
    })

    return groups
}

export const computeTriangulatedPoints = (customPoints: Point[]): Point[] => {
    const nameGroups = _.groupBy(_.filter(customPoints, (p: Point) => !!p.name), (p: Point) => p.name)
    const result: Point[] = []

    _.forEach(nameGroups, (sameNamePoints: Point[], name: string) => {
        if (sameNamePoints.length < 2) {
            return
        }

        const namedOrigin = sameNamePoints[0]
        const namedLocal: LocalObserver[] = _.map(sameNamePoints, (o: Point) => ({
            ...toLocalXY(o.lat, o.lng, namedOrigin.lat, namedOrigin.lng),
            r: Number(o.radius) || 0
        }))

        _.forEach(groupByIntersection(namedLocal), (indices: number[], groupIndex: number) => {
            result.push(...computeGroup(_.at(sameNamePoints, indices), name, groupIndex))
            return
        })
        return
    })

    return result
}

const computeGroup = (observers: Point[], name: string, groupIndex: number): Point[] => {
    debug && console.log(debug, "computeTriangulatedPoints", name, groupIndex, observers)

    const origin = observers[0]
    const local: LocalObserver[] = _.map(observers, (o: Point) => ({
        ...toLocalXY(o.lat, o.lng, origin.lat, origin.lng),
        r: Number(o.radius) || 0
    }))
    const observerIds = _.map(observers, (o: Point) => o.id)

    if (observers.length === 2) {
        const pair = intersectTwoCircles(local[0], local[0].r, local[1], local[1].r)
        if (!pair) {
            return []
        }

        return _.map(pair, (p: LocalPoint, index: number) => {
            const {lat, lng} = toLatLng(p, origin.lat, origin.lng)
            return new Point({
                id                  : "tri-{0}-{1}-probable-{2}".f(name, groupIndex, index),
                name                : "{0} (Вероятные координаты)".f(name),
                coordinates         : "{0},{1}".f(lat, lng),
                radius              : 0,
                isCustom            : false,
                triangulationType   : 'probable',
                observerIds         : observerIds,
            })
        })
    }

    const best = bestFitPoint(local)
    if (!best) {
        return []
    }

    const {lat, lng} = toLatLng(best, origin.lat, origin.lng)
    return [new Point({
        id                  : "tri-{0}-{1}-found".f(name, groupIndex),
        name                : "{0} (Найдено)".f(name),
        coordinates         : "{0},{1}".f(lat, lng),
        radius              : 0,
        isCustom            : false,
        triangulationType   : 'found',
        observerIds         : observerIds,
    })]
}
